import React, { useCallback } from 'react';
import { Card, Button, Header } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserChip } from 'components/lib/Chips';
import util from 'utils/utils';
import { InviteCardProps } from './types';
import InviteeChip from './InviteeChip';

const ProjectInviteCard = ({ invitation, acceptInvite, rejectInvite, removeInvite }: InviteCardProps) => {
  const { inviterUser, inviteeUser, invitee, idea, createdByCurrentUser } = invitation;

  const accept = useCallback(() => {
    util.confirm('Manage idea', `Accept invitation and start managing "${idea.name}" on the project board?`)
      .then(() => {
        acceptInvite(invitation, () => {
          toast.success('Assigned to idea');
        }, () => toast.error('Error while assigning to idea'));
      })
      .catch(() => {})
  }, [acceptInvite, invitation, idea?.name]);

  const reject = useCallback(() => {
    util.confirm('Reject idea assignment', `Are you sure you want to reject the invitation from ${inviterUser?.profile?.fullName} to manage ${idea?.name || 'Deleted idea'} on the project board?`)
      .then(() => {
        rejectInvite(invitation, () => {
          toast('Invitation rejected');
        }, () => toast.error('Error rejecting invite'));
      })
      .catch(() => {})
  }, [rejectInvite, invitation, inviterUser?.profile?.fullName, idea?.name]);

  const remove = useCallback(() => {
    util.confirm('Remove assignment', `Are you sure you want to remove this invitation to let ${inviteeUser?.profile?.fullName || invitee} project manage the idea "${idea?.name || 'Deleted idea'}"?`)
      .then(() => {
        removeInvite(invitation, () => {
          toast('Invitation removed');
        }, () => toast.error('Error removing invite'));
      })
      .catch(() => {})
  }, [removeInvite, invitation, idea?.name, inviteeUser?.profile?.fullName, invitee]);

  if (!idea) {
    invitation.canAccept = false;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <Header style={{ marginBottom: '0.3em' }} as={Link} disabled={!idea} to={idea ? `/ideas/${idea._id}` : null}>{idea?.name || "Deleted idea"}</Header>
        <Card.Description>
          {createdByCurrentUser ? (
            <InviteeChip invitation={invitation} preText='You invited' postText={'to project manage this idea'} />
          ) : (
            <UserChip user={inviterUser} noAvatar postText={`has invited you to project manage this idea`} />
          )}
        </Card.Description>
      </div>
      <Card.Content extra>
        <div className='ui'>
        {invitation.acceptedAt ? (
            <Button basic color='green' as={Link} disabled={!idea} to={`/challenges/${idea?.challenge}/board?idea=${idea?._id}`}>
              Go to board
            </Button>
          ) : (
            <>
              {invitation.canAccept ? (
                <Button basic color='green' onClick={accept}>
                  Accept
                </Button>
              ) : null}
              {invitation.canRemove ? (
                <Button basic color={createdByCurrentUser ? null : 'red'} onClick={createdByCurrentUser ? remove : reject}>
                  {createdByCurrentUser ? 'Remove' : 'Reject'}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  );
}

export default ProjectInviteCard;
